import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { BENIGN_RENDER_WARNING_PATTERNS } from '../utils/globalErrorHandler';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Do not put known harmless React/Syncfusion render warnings (for example 'Node.removeChild')
    // into the error state - the app keeps rendering normally, React retries the subtree itself.
    if (ErrorBoundary.isBenignRenderWarning(error)) {
      return { hasError: false, error: null };
    }
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Do not report harmless render warnings to the error handler pipeline (no toast).
    // The warning stays visible in the browser console (developer debugging).
    if (ErrorBoundary.isBenignRenderWarning(error)) {
      return;
    }

    this.setState({ errorInfo });

    this.handleErrorAsync(error, errorInfo).catch((handlerError) => {
      console.error('Error in ErrorBoundary error handler:', handlerError);
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * React 19 + Syncfusion grid edit popups sometimes produce a
   * 'DOMException: Node.removeChild: The node to be removed is not a child of this node' on dialog unmount.
   * The app keeps working regardless - but we want to show neither the error toast nor
   * the ErrorBoundary fallback screen.
   */
  private static isBenignRenderWarning(error: Error | null | undefined): boolean {
    if (!error) return false;
    const text = `${error.message ?? ''} ${error.stack ?? ''}`;
    return BENIGN_RENDER_WARNING_PATTERNS.some(pattern => text.includes(pattern));
  }

  private async handleErrorAsync(error: Error, errorInfo: ErrorInfo) {
    try {
      const [{ errorHandler }, { ErrorCategory, ErrorSeverity }] = await Promise.all([
        import('../services/ErrorHandler'),
        import('../services/ErrorLogger'),
      ]);

      await errorHandler.handleError(
        error,
        {
          component: 'ErrorBoundary',
          componentStack: errorInfo.componentStack,
        },
        {
          severity: ErrorSeverity.HIGH,
          category: ErrorCategory.RUNTIME,
          userMessage: 'Ein Fehler ist in dieser Komponente aufgetreten. Die Seite wurde möglicherweise nicht korrekt geladen.',
          showToast: false,
          preventBrowserError: true,
        }
      );
    } catch (importError) {
      console.error('ErrorBoundary: Failed to load error handler modules:', importError);
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-[400px] p-8">
          <div className="max-w-lg w-full bg-white rounded-lg shadow-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-800">
                Ein unerwarteter Fehler ist aufgetreten
              </h2>
            </div>

            <p className="text-sm text-gray-600 mb-5">
              Die Seite konnte nicht korrekt geladen werden. Bitte laden Sie die Seite neu.
              Sollte das Problem weiterhin auftreten, wenden Sie sich an den Support.
            </p>

            {import.meta.env.MODE === 'development' && this.state.error && (
              <details className="mb-5">
                <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600">
                  Technische Details (nur in Development)
                </summary>
                <pre className="mt-2 bg-gray-50 border border-gray-200 p-3 rounded text-xs overflow-auto max-h-48 whitespace-pre-wrap break-words text-gray-700">
                  {this.state.error.stack}
                  {this.state.errorInfo?.componentStack && (
                    <>
                      {'\n\nComponent Stack:\n'}
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => window.location.reload()}
                style={{ backgroundColor: '#09584b', borderColor: '#09584b', color: '#fff' }}
                className="px-5 py-2 text-sm font-medium rounded transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
